import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/base/PageHeader";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import ContentCard from "@/components/feature/ContentCard";
import { analysisItems, sortByDate } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

export default function AnalysisPage() {
  usePageMeta({
    title: "剧情解析 - 星空动漫",
    description:
      "星空动漫剧情解析频道提供伏笔梳理、结局解读、主题探讨与创作技巧分析，按作品名与主题标签筛选，深入理解每一部作品的设计。",
    keywords: "剧情解析,结局解读,伏笔分析,主题探讨,动漫解析,星空动漫",
  });

  const [tag, setTag] = useState("all");
  const [page, setPage] = useState(1);

  const tags = useMemo(() => {
    const set = new Set<string>();
    analysisItems.forEach((item) => item.tags.forEach((t) => set.add(t)));
    return Array.from(set).slice(0, 12);
  }, []);

  const filtered = useMemo(() => {
    const list = sortByDate(analysisItems);
    if (tag === "all") return list;
    return list.filter((item) => item.tags.includes(tag));
  }, [tag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <SiteLayout>
      <div className="mx-auto max-w-page px-4 py-8 md:px-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "剧情解析" }]} />

        <div className="mt-4">
          <PageHeader
            title="剧情解析"
            description="好看的动画值得被认真讨论。我们从剧情结构、伏笔设计、主题表达与创作技巧几个角度出发，带你更深入地读懂作品。点击标签可按作品或主题筛选。"
          />
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setTag("all");
              setPage(1);
            }}
            className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
              tag === "all"
                ? "border-primary-500 bg-primary-500 text-background-50"
                : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            全部
          </button>
          {tags.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => {
                setTag(item);
                setPage(1);
              }}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                tag === item
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {pageItems.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {pageItems.map((item) => (
                <ContentCard key={item.id} item={item} variant="col" />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-article-line"
              title="该标签下暂无解析"
              description="换一个标签看看，或者浏览全部解析文章。"
              actionLabel="查看全部"
              onAction={() => {
                setTag("all");
                setPage(1);
              }}
            />
          )}
        </div>

        {pageItems.length > 0 ? (
          <div className="mt-8">
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>
        ) : null}
      </div>
    </SiteLayout>
  );
}